import { Badge } from "@/components/ui/badge";

import type { FramingSceneInspector } from "../framingInspector";
import {
  FRAMING_VERDICT_STEPS,
  buildSubjectVerdicts,
  planAppliedComparison,
  type FramingVerdictValue,
} from "./framingSubjectVerdicts";

const STEP_LABELS: Record<(typeof FRAMING_VERDICT_STEPS)[number], string> = {
  targeted: "Targeted",
  observed: "Observed",
  protected: "Protected",
  inside_plan: "Inside plan",
  inside_applied: "Inside applied",
  delivered: "Delivered",
};

const VALUE_GLYPHS: Record<FramingVerdictValue, string> = {
  yes: "✓ yes",
  no: "✗ no",
  not_evaluated: "· not evaluated",
  ambiguous: "? ambiguous",
  unavailable: "– unavailable",
};

const VALUE_CLASSES: Record<FramingVerdictValue, string> = {
  yes: "text-emerald-600",
  no: "text-destructive font-medium",
  not_evaluated: "text-muted-foreground",
  ambiguous: "text-amber-600",
  unavailable: "text-muted-foreground",
};

/**
 * Pseudonymous subject-verdict matrix (CROP-WB-008). Values use text and
 * glyphs, never color alone, and every cell names its evidence source.
 */
export function FramingSubjectVerdictTable({
  inspector,
}: {
  inspector: FramingSceneInspector;
}) {
  const rows = buildSubjectVerdicts(inspector);
  const comparison = planAppliedComparison(inspector);
  if (rows.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        No pseudonymous subject intent was recorded for this scene.
      </p>
    );
  }
  return (
    <div className="space-y-2">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[640px] text-xs">
          <caption className="sr-only">
            Subject verdict chain from targeted through delivered
          </caption>
          <thead>
            <tr className="border-b text-left text-muted-foreground">
              <th scope="col" className="py-1 pr-2">
                Subject
              </th>
              {FRAMING_VERDICT_STEPS.map((step) => (
                <th key={step} scope="col" className="py-1 pr-2">
                  {STEP_LABELS[step]}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.subjectId} className="border-b align-top">
                <th scope="row" className="py-1 pr-2 text-left font-normal">
                  <div className="font-medium">
                    {row.subjectId} · {row.kind}
                  </div>
                  <div className="text-muted-foreground">
                    {row.priority ?? "priority unknown"}
                    {row.identityUnverified ? (
                      <Badge variant="outline" className="ml-1">
                        identity unverified
                      </Badge>
                    ) : null}
                  </div>
                  {row.visibleFraction != null ? (
                    <div className="text-muted-foreground">
                      visible {Math.round(row.visibleFraction * 100)}%
                      {row.minEdgeMargin != null
                        ? ` · margin ${(row.minEdgeMargin * 100).toFixed(1)}%`
                        : ""}
                    </div>
                  ) : null}
                </th>
                {FRAMING_VERDICT_STEPS.map((step) => {
                  const cell = row.steps[step];
                  return (
                    <td key={step} className="py-1 pr-2">
                      <span className={VALUE_CLASSES[cell.value]}>
                        {VALUE_GLYPHS[cell.value]}
                      </span>
                      <div className="text-[10px] text-muted-foreground">
                        {cell.evidence}
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {rows.some((row) => row.identityUnverified) ? (
        <p className="text-[11px] text-muted-foreground">
          Identity unverified means geometry and semantic continuity were
          measured without identity recognition; it is not by itself a crop
          failure.
        </p>
      ) : null}
      <p className="text-[11px] text-muted-foreground">
        {comparison.comparable
          ? `Planned vs applied IoU ${comparison.iou!.toFixed(3)} (same coordinate root).`
          : `Planned vs applied not comparable: ${comparison.reason}`}
      </p>
    </div>
  );
}
